import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import { clarityEvent } from '../utils/clarity';
import './QuoteViewPage.css';

const fmt = (n) =>
    n != null
        ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n)
        : '—';

// ── PRICING MODEL — read before touching lineAmounts ─────────────────────────
// eYantra operates across two pricing worlds:
//
//   BRAND WORLD   — MRP comes directly from brands, always INCLUSIVE of GST.
//                   Never add GST to mrp. Use it as-is for display.
//
//   TRADE WORLD   — marketOperatingPrice and eYantraDiscountedPrice are
//                   negotiated with vendors, always EXCLUSIVE of GST.
//                   Always add GST before displaying to the client.
//
// What the backend sends on each quote item:
//   item.price                   → eYantraDiscountedPrice (excl. GST)  if employee set a custom price
//                                  OR marketOperatingPriceWithGst (incl. GST) as fallback
//   item.marketOperatingPriceWithGst → standard MOP incl. GST (always incl. GST, used for strikethrough)
//   item.mrp                     → brand MRP (always incl. GST, ceiling price)
//
// Detection: when item.price < item.marketOperatingPriceWithGst, it must be
// the excl-GST discount price → add GST. Otherwise it's already incl. GST → use as-is.
// ─────────────────────────────────────────────────────────────────────────────
// One line's amounts. Shared by the table rows and the totals so they can never disagree.
const lineAmounts = (item) => {
    const gstRate  = item.gst || 0;
    const qty      = item.quantity || 0;
    const factor   = 1 + gstRate / 100;
    // A discounted price entered on the line is always used (excl. GST); otherwise the normal
    // price. The backend says which via item.discounted; the price comparison below is only the
    // fallback for an older backend that doesn't send it.
    const isDiscounted = item.discounted != null ? item.discounted === true : !!(item.price && item.marketOperatingPriceWithGst
        && item.price < item.marketOperatingPriceWithGst);

    let rateExcl, taxable, gst, total;
    if (isDiscounted) {
        // For discounted items item.price is excl. GST — compute from it directly to avoid the
        // rounding loss of back-calculating base from a rounded incl. price.
        rateExcl = item.price;
        taxable  = item.price * qty;
        gst      = Math.round(taxable * gstRate / 100);
        total    = taxable + gst;
    } else {
        const unitIncl = Math.round(item.price || item.marketOperatingPriceWithGst || 0);
        rateExcl = unitIncl / factor;
        total    = unitIncl * qty;
        taxable  = total / factor;
        gst      = total - taxable;
    }
    return { gstRate, qty, rateExcl, taxable, gst, total };
};

// Indian-system amount in words, e.g. 3650 → "Three Thousand Six Hundred Fifty".
const ONES = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven',
    'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
const twoDigits = (n) => n < 20 ? ONES[n] : `${TENS[Math.floor(n / 10)]}${n % 10 ? ' ' + ONES[n % 10] : ''}`;
const threeDigits = (n) => [n >= 100 ? `${ONES[Math.floor(n / 100)]} Hundred` : '', twoDigits(n % 100)].filter(Boolean).join(' ');
const numberToWords = (num) => {
    let n = Math.floor(num);
    if (n === 0) return 'Zero';
    const parts = [];
    const crore = Math.floor(n / 10000000); n %= 10000000;
    const lakh = Math.floor(n / 100000);    n %= 100000;
    const thousand = Math.floor(n / 1000);  n %= 1000;
    if (crore)    parts.push(`${numberToWords(crore)} Crore`);
    if (lakh)     parts.push(`${twoDigits(lakh)} Lakh`);
    if (thousand) parts.push(`${twoDigits(thousand)} Thousand`);
    if (n)        parts.push(threeDigits(n));
    return parts.join(' ');
};
const amountInWords = (amount) => {
    const rounded = Math.round((amount || 0) * 100) / 100;
    const rupees = Math.floor(rounded);
    const paise = Math.round((rounded - rupees) * 100);
    return `Indian Rupees ${numberToWords(rupees)}${paise ? ` and ${twoDigits(paise)} Paise` : ''} Only`;
};

// ── Quote line (one table row; stacks into a card on phones) ──
// Client-facing: shows only the quoted rate — no MRP, standard price or "special price" notes.
const QuoteLine = ({ item, index }) => {
    const a = lineAmounts(item);
    return (
        <tr className="qvp-line">
            <td className="qvp-line__num">{index + 1}</td>
            <td className="qvp-line__item">
                <div className="qvp-line__item-inner">
                    <div className="qvp-line__img">
                        {item.thumbnailUrl ? <img src={item.thumbnailUrl} alt="" /> : <span>📦</span>}
                    </div>
                    <div>
                        <div className="qvp-line__name">{item.name}</div>
                        {item.variantLabel && <span className="qvp-line__variant">{item.variantLabel}</span>}
                        {item.sku && <div className="qvp-line__sku">SKU: {item.sku}</div>}
                    </div>
                </div>
            </td>
            <td className="qvp-line__num-cell" data-label="Qty">{a.qty}</td>
            <td className="qvp-line__num-cell" data-label="Rate (excl. GST)">{fmt(a.rateExcl)}</td>
            <td className="qvp-line__num-cell" data-label="Taxable value">{fmt(a.taxable)}</td>
            <td className="qvp-line__num-cell" data-label={`GST @ ${a.gstRate}%`}>
                <span className="qvp-line__gst-rate">{a.gstRate}%</span>
                <span className="qvp-line__gst-amt">{fmt(a.gst)}</span>
            </td>
            <td className="qvp-line__num-cell qvp-line__total" data-label="Amount (incl. GST)">{fmt(a.total)}</td>
        </tr>
    );
};

// ── Main page ─────────────────────────────────────────────────
const QuoteViewPage = () => {
    const { user } = useAuth();
    const [searchParams] = useSearchParams();
    const prefillName = searchParams.get('name') || '';

    // Gate form state
    const [cartName,     setCartName]     = useState(prefillName);
    const [password,     setPassword]     = useState('');
    const [contactInfo,  setContactInfo]  = useState('');
    const [showPass,     setShowPass]     = useState(false);
    const [isLoading,    setIsLoading]    = useState(false);
    const [error,        setError]        = useState('');

    // Quote data state
    const [quote,        setQuote]        = useState(null);
    const printRef = useRef(null);

    useEffect(() => {
        if (user && prefillName && !quote) {
            setIsLoading(true);
            api.post('/quotes/access', {
                cartName: prefillName,
                password: '',
                contactInfo: user.email
            })
            .then(res => {
                setQuote(res.data);
                clarityEvent('quote_viewed');
                setIsLoading(false);
            })
            .catch(() => {
                setIsLoading(false);
            });
        }
    }, [user, prefillName, quote]);

    const handleAccess = async (e) => {
        e.preventDefault();
        if (!cartName.trim())    { setError('Please enter the cart name.'); return; }
        if (!password.trim())    { setError('Please enter the password.'); return; }
        if (!contactInfo.trim()) { setError('Please enter your contact info (email or phone).'); return; }
        setError('');
        setIsLoading(true);
        try {
            const res = await api.post('/quotes/access', {
                cartName: cartName.trim(),
                password,
                contactInfo: contactInfo.trim(),
            });
            setQuote(res.data);
        } catch (err) {
            if (err.response?.status === 401) {
                setError('Invalid cart name or password. Please check and try again.');
            } else {
                setError('Something went wrong. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handlePrint = () => {
        const originalTitle = document.title;
        document.title = `eYantra_Quote_${quote.cartName}`;
        window.print();
        setTimeout(() => {
            document.title = originalTitle;
        }, 500);
    };

    // ── Totals Calculation ── (same lineAmounts as the rows, plus a GST split by rate)
    const { totalBase, totalGst, grandTotal, gstByRate, unitCount } = useMemo(() => {
        if (!quote || !quote.items) return { totalBase: 0, totalGst: 0, grandTotal: 0, gstByRate: [], unitCount: 0 };
        const byRate = new Map();
        const itemTotals = quote.items.reduce((acc, item) => {
            const a = lineAmounts(item);
            const r = byRate.get(a.gstRate) || { rate: a.gstRate, taxable: 0, gst: 0 };
            r.taxable += a.taxable; r.gst += a.gst;
            byRate.set(a.gstRate, r);
            return {
                totalBase:  acc.totalBase  + a.taxable,
                totalGst:   acc.totalGst   + a.gst,
                grandTotal: acc.grandTotal + a.total,
                unitCount:  acc.unitCount  + a.qty,
            };
        }, { totalBase: 0, totalGst: 0, grandTotal: 0, unitCount: 0 });

        const deliveryCharge = quote.deliveryCharge || 0;
        const customizationCharge = quote.customizationCharge || 0;
        return {
            ...itemTotals,
            gstByRate: [...byRate.values()].sort((x, y) => x.rate - y.rate),
            grandTotal: itemTotals.grandTotal + deliveryCharge + customizationCharge,
        };
    }, [quote]);

    // ── Gate screen ───────────────────────────────────────────
    if (!quote) {
        // [KEEP YOUR EXISTING GATE RENDER LOGIC HERE]
        return (
            <div className="qvp-gate-wrapper">
                <div className="qvp-gate-bg" />
                <div className="qvp-gate-card">
                    <div className="qvp-gate-logo"><div className="qvp-gate-logo__icon">📋</div></div>
                    <h1 className="qvp-gate-title">View Your Quote</h1>
                    <p className="qcp-gate-sub">Enter the cart name and password shared by your representative, along with your contact info.</p>
                    <form className="qvp-gate-form" onSubmit={handleAccess}>
                        <div className="qvp-gate-field">
                            <label>Cart Name</label>
                            <input type="text" placeholder="e.g. Acme-Corp-Q1-2025" value={cartName} onChange={e => setCartName(e.target.value)} autoFocus={!prefillName} />
                        </div>
                        <div className="qvp-gate-field">
                            <label>Password</label>
                            <div className="qvp-gate-pass-wrap">
                                <input type={showPass ? 'text' : 'password'} placeholder="Enter password" value={password} onChange={e => setPassword(e.target.value)} />
                                <button type="button" onClick={() => setShowPass(s => !s)}>{showPass ? '🙈' : '👁'}</button>
                            </div>
                        </div>
                        <div className="qvp-gate-field">
                            <label>Your Contact Info <span>(email or phone)</span></label>
                            <input type="text" placeholder="your@email.com or +91 XXXXX XXXXX" value={contactInfo} onChange={e => setContactInfo(e.target.value)} />
                            <div className="qvp-gate-field__hint">This is recorded so we know who viewed the quote.</div>
                        </div>
                        {error && <div className="qvp-gate-error">{error}</div>}
                        <button type="submit" className="qvp-gate-submit" disabled={isLoading}>
                            {isLoading ? <span className="qvp-gate-spinner" /> : <>🔓 View My Quote</>}
                        </button>
                    </form>
                    <div className="qvp-gate-footer"><span>🔒 Secure · Private · View-only</span></div>
                </div>
            </div>
        );
    }

    // ── Quote display ─────────────────────────────────────────
    const today = new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });

    // Helper: only render a meta-row if the value is non-empty
    const hasRef = quote.modeTermsOfPayment || quote.suppliersRef || quote.otherReferences ||
        quote.buyerOrderNo || quote.buyerOrderDated || quote.despatchDocumentNo ||
        quote.destination || quote.despatchedType || quote.paymentTerms || quote.termsOfDelivery;

    return (
        <div className="qvp-quote-wrapper" ref={printRef}>
            <div className="qvp-quote-bg" />
            <div className="qvp-quote-doc">
                <div className="qvp-doc-header">
                    <div className="qvp-doc-header__left">
                        <div className="qvp-doc-brand">eYantra</div>
                        <div className="qvp-doc-tagline">Corporate Swag & Premium Gifting</div>
                    </div>
                    <div className="qvp-doc-header__right">
                        <div className="qvp-doc-meta-row">
                            <span className="qvp-doc-meta-label">QUOTE REFERENCE</span>
                            <span className="qvp-doc-meta-val qvp-doc-meta-val--highlight">{quote.cartName}</span>
                        </div>
                        <div className="qvp-doc-meta-row">
                            <span className="qvp-doc-meta-label">DATE</span>
                            <span className="qvp-doc-meta-val">{today}</span>
                        </div>
                        <div className="qvp-doc-meta-row">
                            <span className="qvp-doc-meta-label">PREPARED FOR</span>
                            <span className="qvp-doc-meta-val">{quote.quoteCreatedFor || 'Client'}</span>
                        </div>
                    </div>
                </div>
                <div className="qvp-doc-divider" />

                {/* ── Buyer details (shown when filled) ─────────── */}
                {(quote.billingAddress || quote.clientGstin || quote.clientPhone) && (
                    <div className="qvp-buyer-block">
                        <div className="qvp-buyer-block__label">BILL TO</div>
                        <div className="qvp-buyer-block__name">{quote.quoteCreatedFor || '—'}</div>
                        {quote.billingAddress && <div className="qvp-buyer-block__addr">{quote.billingAddress}</div>}
                        <div className="qvp-buyer-block__meta-row">
                            {quote.clientPhone && <span>Ph: {quote.clientPhone}</span>}
                            {quote.clientGstin && <span>GSTIN: {quote.clientGstin}</span>}
                        </div>
                    </div>
                )}

                {/* ── Reference fields (shown when at least one is filled) ── */}
                {hasRef && (
                    <div className="qvp-ref-grid">
                        {quote.modeTermsOfPayment  && <div className="qvp-ref-item"><span className="qvp-ref-item__label">Mode / Terms of Payment</span><span className="qvp-ref-item__val">{quote.modeTermsOfPayment}</span></div>}
                        {quote.paymentTerms        && <div className="qvp-ref-item"><span className="qvp-ref-item__label">Payment Terms</span><span className="qvp-ref-item__val">{quote.paymentTerms}</span></div>}
                        {quote.suppliersRef        && <div className="qvp-ref-item"><span className="qvp-ref-item__label">Supplier's Ref.</span><span className="qvp-ref-item__val">{quote.suppliersRef}</span></div>}
                        {quote.otherReferences     && <div className="qvp-ref-item"><span className="qvp-ref-item__label">Other Reference(s)</span><span className="qvp-ref-item__val">{quote.otherReferences}</span></div>}
                        {quote.buyerOrderNo        && <div className="qvp-ref-item"><span className="qvp-ref-item__label">Buyer's Order No.</span><span className="qvp-ref-item__val">{quote.buyerOrderNo}</span></div>}
                        {quote.buyerOrderDated     && <div className="qvp-ref-item"><span className="qvp-ref-item__label">Buyer Order Dated</span><span className="qvp-ref-item__val">{quote.buyerOrderDated}</span></div>}
                        {quote.despatchDocumentNo  && <div className="qvp-ref-item"><span className="qvp-ref-item__label">Despatch Document No.</span><span className="qvp-ref-item__val">{quote.despatchDocumentNo}</span></div>}
                        {quote.destination         && <div className="qvp-ref-item"><span className="qvp-ref-item__label">Destination</span><span className="qvp-ref-item__val">{quote.destination}</span></div>}
                        {quote.despatchedType      && <div className="qvp-ref-item"><span className="qvp-ref-item__label">Despatched through</span><span className="qvp-ref-item__val">{quote.despatchedType}</span></div>}
                        {quote.termsOfDelivery     && <div className="qvp-ref-item"><span className="qvp-ref-item__label">Terms of Delivery</span><span className="qvp-ref-item__val">{quote.termsOfDelivery}</span></div>}
                    </div>
                )}

                <div className="qvp-section-head">
                    <h2 className="qvp-section-head__title">Quoted Items</h2>
                    <span className="qvp-section-head__count">
                        {quote.items.length} {quote.items.length === 1 ? 'product' : 'products'} · {unitCount} {unitCount === 1 ? 'unit' : 'units'}
                    </span>
                </div>
                <p className="qvp-lines-hint">Rates are per unit and exclude GST. GST is added on each item at its own rate.</p>

                <div className="qvp-lines-wrap">
                    <table className="qvp-lines">
                        <thead>
                            <tr>
                                <th className="qvp-line__num">#</th>
                                <th>Item</th>
                                <th className="qvp-line__num-cell">Qty</th>
                                <th className="qvp-line__num-cell">Rate<small>excl. GST</small></th>
                                <th className="qvp-line__num-cell">Taxable value</th>
                                <th className="qvp-line__num-cell">GST</th>
                                <th className="qvp-line__num-cell">Amount<small>incl. GST</small></th>
                            </tr>
                        </thead>
                        <tbody>
                            {quote.items.map((item, i) => (
                                <QuoteLine key={`${item.productId}-${i}`} item={item} index={i} />
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="qvp-summary">
                    <div className="qvp-summary__box">
                        <div className="qvp-summary__row">
                            <span>Taxable value <small>(excl. GST)</small></span>
                            <span>{fmt(totalBase)}</span>
                        </div>
                        {gstByRate.map(g => (
                            <div key={g.rate} className="qvp-summary__row qvp-summary__row--sub">
                                <span>GST @ {g.rate}% <small>on {fmt(g.taxable)}</small></span>
                                <span>{fmt(g.gst)}</span>
                            </div>
                        ))}
                        {gstByRate.length > 1 && (
                            <div className="qvp-summary__row">
                                <span>Total GST</span>
                                <span>{fmt(totalGst)}</span>
                            </div>
                        )}
                        {quote.deliveryCharge > 0 && (
                            <div className="qvp-summary__row">
                                <span>Delivery charges</span>
                                <span>{fmt(quote.deliveryCharge)}</span>
                            </div>
                        )}
                        {quote.customizationCharge > 0 && (
                            <div className="qvp-summary__row">
                                <span>Customization charges</span>
                                <span>{fmt(quote.customizationCharge)}</span>
                            </div>
                        )}
                        <div className="qvp-summary__grand">
                            <span>Grand Total <small>incl. GST</small></span>
                            <span className="qvp-summary__grand-val">{fmt(grandTotal)}</span>
                        </div>
                        <div className="qvp-summary__words">{amountInWords(Math.round(grandTotal))}</div>
                        <div className="qvp-totals__eoe">E &amp; O.E</div>
                    </div>
                </div>

                {/* ── Bank details + signature ───────────────────── */}
                <div className="qvp-bank-sign-row">
                    <div className="qvp-bank">
                        <div className="qvp-bank__title">Company's Bank Details</div>
                        <div className="qvp-bank__row"><span>Bank Name</span><span>AU Small Finance Bank</span></div>
                        <div className="qvp-bank__row"><span>A/c No.</span><span>2606266799636981</span></div>
                        <div className="qvp-bank__row"><span>Branch</span><span>Madhapur, Hyderabad</span></div>
                        <div className="qvp-bank__row"><span>IFSC Code</span><span>AUBL0002667</span></div>
                        <div className="qvp-bank__pan">Company's PAN: <strong>AAACP2025M</strong></div>
                    </div>
                    <div className="qvp-sign">
                        <div className="qvp-sign__for">For eYantra Ventures Limited</div>
                        <div className="qvp-sign__spacer" />
                        <div className="qvp-sign__line" />
                        <div className="qvp-sign__label">Authorised Signatory</div>
                        {quote.preparedBy && <div className="qvp-sign__prepared">Prepared by: {quote.preparedBy}</div>}
                    </div>
                </div>

                <div className="qvp-doc-footer">
                    <div className="qvp-doc-footer__note">
                        <strong>Note:</strong> Final pricing may vary based on order quantity, customisation and delivery terms. This quote is valid for 30 days.
                    </div>
                    <div className="qvp-doc-footer__actions no-print">
                        <button className="qvp-action-btn qvp-action-btn--print" onClick={handlePrint}>🖨 Print / Save PDF</button>
                        <button className="qvp-action-btn qvp-action-btn--share" onClick={() => navigator.clipboard.writeText(window.location.href)}>🔗 Copy Link</button>
                    </div>
                    <div className="qvp-doc-footer__brand">eYantra · Corporate Gifting & Swag · Pan-India Delivery</div>
                </div>
            </div>
        </div>
    );
};

export default QuoteViewPage;